import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  BANDS_DJS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  VendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import {
  getVendorWordPlural,
  getVendorWordSingular,
} from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { isDesktop, isXlDesktop } from '@zola-helpers/client/dist/es/util/responsive';
import { setLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import { InquiryDraftCardView, StorefrontCardView } from '@zola/svc-marketplace-ts-types';
import { ErrorBoundary } from '@zola/zola-ui/src/components/ErrorBoundary';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import HighlightTitle from '@zola/zola-ui/src/components/PreAuthV3/HighlightTitle';
import VendorsCategoryList from '@zola/zola-ui/src/components/VendorsCategoryList';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';
import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';

import cx from 'classnames';

import { BookVendorModalFormValues } from '~/actions/bookedVendorsActions';
import { loadMiniQuizAnswers } from '~/actions/couples/miniQuizActions';
import { storefrontCardViewToVendorCardVendor } from '~/components/common/cards/util/helper';
import VendorCard from '~/components/common/cards/VendorCard/VendorCard';
import BookVendorModal from '~/components/common/modals/BookVendorModal';
import { fetchFirstMovePreferences } from '~/components/common/modals/InvitesFromVendorsOptIn/invitesFromVendors';
import VendorsByCategory from '~/components/VendorsByCategory/VendorsByCategory';
import { useModal } from '~/contexts/ModalContext';
import { doTrackStorefrontListViewed } from '~/contexts/TrackingContracts/trackingContractsHelpers';
import { useToast } from '~/hooks/useToast';
import { useAppDispatch } from '~/reducers';
import { VendorLandingPageView } from '~/types/responseTypes';
import ApiService from '~/util/apiService';
import { StandardLogFn } from '~/util/logger';
import { TrackingProductLocation } from '~/util/trackingHelper';
import vendorSearchUrl from '~/util/vendorSearchUrl';

import { NotNullMembersBriefLocation } from '../../util/types';
import { OverlaySearchBar } from '../OverlaySearchBar/OverlaySearchBar';
import { PageSection } from '../PageSection/PageSection';
import EditLocationModal from '../SearchBar/EditLocationModal';
import { MiniQuizCard } from '../VendorCardGrid/MiniQuizCard/MiniQuizCard';
import RecentlyViewed from './components/RecentlyViewed';
import Shelf from './components/Shelf';
import SideScrollingCardList from './components/SideScrollingCardList';
import SmallVendorCardList from './components/SmallVendorCardList';
import { useRecentlyViewedStorefronts } from './contexts/RecentlyViewedStorefrontsContext';
import { RecentlyViewedStorefrontsProvider } from './contexts/RecentlyViewedStorefrontsProvider';
import { fetchLandingPage } from './utils/helpers';

import styles from './personalizedContent.module.less';

const Content = ({
  briefLocation,
  setHasPersonalizedContentLoaded,
  isClpTest,
}: {
  briefLocation: NotNullMembersBriefLocation;
  setHasPersonalizedContentLoaded: Dispatch<SetStateAction<boolean>>;
  isClpTest: boolean;
}) => {
  const [viewData, setViewData] = useState<VendorLandingPageView | null>(null);
  const [topInquiredList, setTopInquiredList] = useState<StorefrontCardView[] | null>(null);
  const [inquiryDraftStorefrontList, setInquiryDraftStorefrontList] = useState<
    StorefrontCardView[] | null
  >(null);
  const [favoriteStorefrontList, setFavoriteStorefrontList] = useState<StorefrontCardView[] | null>(
    null
  );
  const [showMTFMmodule, setShowMTFMmodule] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const dispatch = useAppDispatch();
  const { showCustomModal } = useModal();
  const { positiveToast } = useToast();

  const loadLandingPage = useCallback(
    () =>
      fetchLandingPage({
        vendor_market_id: briefLocation.vendorMarket.id,
      }),
    [briefLocation.vendorMarket.id]
  );

  useEffectOnce(() => {
    loadLandingPage().then(setViewData).catch(StandardLogFn);
  });

  const getMTFMoptin = useCallback(() => {
    return fetchFirstMovePreferences(['FIRST_MOVES_SHARE_WEDDING_DETAILS']).then((preferences) => {
      setShowMTFMmodule(!preferences.FIRST_MOVES_SHARE_WEDDING_DETAILS?.enabled);
    });
  }, []);

  const onMiniQuizSubmited = useCallback(() => {
    getMTFMoptin()
      .then(() => {
        dispatch(
          positiveToast({
            headline: 'All set! Their hellos will appear in your inbox',
            autoDismissInSeconds: 3,
          })
        );
      })
      .catch(() => null);
  }, [dispatch, getMTFMoptin, positiveToast]);

  useEffectOnce(() => {
    getMTFMoptin().catch(() => null);
  });

  useEffect(() => {
    if (
      viewData?.alsoInquiredSameCategory &&
      viewData.alsoInquiredSameCategory.length > 0 &&
      viewData.mostRecentInquiry?.storefront.taxonomyNode.key
    ) {
      doTrackStorefrontListViewed({
        storefrontList: viewData.alsoInquiredSameCategory,
        taxonomyKey: viewData.mostRecentInquiry.storefront.taxonomyNode
          .key as SearchableVendorTaxonomyKey,
      });
    }
  }, [
    viewData?.alsoInquiredSameCategory,
    viewData?.mostRecentInquiry?.storefront.taxonomyNode.key,
  ]);

  useEffect(() => {
    if (viewData?.alsoBookedCrossCategory && viewData.alsoBookedCrossCategory.length > 0) {
      doTrackStorefrontListViewed({
        storefrontList: viewData.alsoBookedCrossCategory,
      });
    }
  }, [viewData?.alsoBookedCrossCategory]);

  const onBookingSuccess = useCallback(
    (values: BookVendorModalFormValues) => {
      const { vendorName } = values;
      positiveToast({
        headline: `We've marked ${vendorName || 'this vendor'} as booked!`,
      });
      loadLandingPage()
        .then((landingPage) => {
          if (landingPage.alsoBookedCrossCategory.length) {
            // refresh cross category recs if a new set comes back based on the booking
            setViewData(landingPage);
          }
        })
        .catch(StandardLogFn);
    },
    [loadLandingPage, positiveToast]
  );

  const onBookedVendorsClick = useCallback(
    (vendorTaxonomyKey: VendorTaxonomyKey) => {
      showCustomModal(BookVendorModal, {
        section: 'LANDING_PAGE',
        location: TrackingProductLocation.VENDOR_LANDING_PAGE,
        vendorTaxonomyKey,
        handleSuccess: onBookingSuccess,
      });
    },
    [onBookingSuccess, showCustomModal]
  );

  const topPicksDisplayCount = useMemo(() => {
    let count = 4;
    if (showMTFMmodule) {
      count -= 1;
    }
    if (isXlDesktop()) {
      count += 1;
    }
    return count;
  }, [showMTFMmodule]);

  useEffect(() => {
    if (viewData?.topPicks && viewData.topPicks.length > 0 && showMTFMmodule) {
      dispatch(loadMiniQuizAnswers(VENUES_TAXONOMY_KEY, false)).catch(() => null);
    }
  }, [viewData?.topPicks, dispatch, showMTFMmodule]);

  // if a user has both drafts & favorites, show a max of 2 (or 3 on screens > 1570px wide)
  // draft cards and fill the rest of the space with favorite cards
  useEffect(() => {
    const showExtraSmallVendorCard = window.matchMedia('(min-width: 1570px)').matches;
    const hasDrafts = viewData?.inquiryDrafts && viewData.inquiryDrafts.length > 0;
    const hasFavorites = viewData?.nonInquiredFavorites && viewData.nonInquiredFavorites.length > 0;
    let draftCount = 0;
    let favoriteCount = 0;

    if (viewData?.inquiryDrafts && viewData.inquiryDrafts.length > 0) {
      draftCount = showExtraSmallVendorCard ? 4 : 3;
      if (hasFavorites) {
        draftCount -= 1;
      }
    }

    const draftList = hasDrafts
      ? (viewData.inquiryDrafts as InquiryDraftCardView[])
          .slice(0, draftCount)
          .map((draft) => draft.card)
          .filter((card) => !!card)
      : null;
    setInquiryDraftStorefrontList(draftList as StorefrontCardView[] | null);

    if (hasFavorites) {
      favoriteCount = 4 - ((draftList && draftList.length) || 0);
      if (!showExtraSmallVendorCard) {
        favoriteCount -= 1;
      }
      setFavoriteStorefrontList(
        viewData?.nonInquiredFavorites?.slice(0, favoriteCount) as StorefrontCardView[] | null
      );
    }
  }, [viewData?.inquiryDrafts, viewData?.nonInquiredFavorites]);

  useEffect(() => {
    if (briefLocation.vendorMarket.id && !topInquiredList) {
      ApiService.get<StorefrontCardView[]>(
        `/web-marketplace-api/v3/storefront/most-inquired?vendor_market_id=${briefLocation.vendorMarket.id}`
      )
        .then((list) => {
          setTopInquiredList(list);
        })
        .catch(StandardLogFn);
    }
  }, [briefLocation, topInquiredList]);

  const onInquiryDraftCompletion = useCallback(() => {
    loadLandingPage().then(setViewData).catch(StandardLogFn);
  }, [loadLandingPage]);

  const { ready: recentlyViewedReady } = useRecentlyViewedStorefronts();

  useEffect(() => {
    if (viewData && recentlyViewedReady) {
      setHasPersonalizedContentLoaded(true);
    }
  }, [viewData, recentlyViewedReady, setHasPersonalizedContentLoaded]);

  if (!viewData || !recentlyViewedReady) {
    return <div className={cx(styles.personalizedContent, styles.empty)} data-testid="empty" />;
  }

  const renderCategoryList = () =>
    isClpTest ? (
      <>
        <VendorsByCategory
          isClpTest={isClpTest}
          showModal={() => setShowLocationModal(true)}
          hideHed
        />
        {showLocationModal && <EditLocationModal hideModalFn={() => setShowLocationModal(false)} />}
      </>
    ) : (
      <VendorsCategoryList className={styles.categoryList} />
    );

  return (
    <div className={styles.personalizedContent}>
      {viewData.mostRecentInquiry && (
        <div className={styles.hero}>
          <div className={styles.heroWithoutValueProps}>
            <div className={styles.overlaySearchBarWrapper}>
              <OverlaySearchBar isClpTest={isClpTest} />
            </div>
            <PageSection className={styles.categoryListContainer}>
              {renderCategoryList()}
            </PageSection>
          </div>
        </div>
      )}

      {!viewData.mostRecentInquiry && (
        <div className={styles.hero}>
          <div className={styles.heroWithValueProps}>
            <div className={styles.valueProps}>
              <H.Title1 presentation="h5">Wedding venues and vendors</H.Title1>
              <HighlightTitle isH1={false}>
                Let&rsquo;s find your (other) <strong>perfect match</strong>
              </HighlightTitle>
              <ul>
                <li>
                  <H.Title4 presentation="h5">
                    Discover, inquire with, and book vendors who are right for you
                  </H.Title4>
                </li>
                <li>
                  <H.Title4 presentation="h5">
                    Transparent pricing and vendors for every budget
                  </H.Title4>
                </li>
                <li>
                  <H.Title4 presentation="h5">Plus, personalized recs to match your style</H.Title4>
                </li>
              </ul>
            </div>

            <OverlaySearchBar
              verticalStacked
              weddingLocation={briefLocation}
              includeSavedSearch
              className={styles.overlaySearchbar}
            />
          </div>
          <PageSection>{renderCategoryList()}</PageSection>
        </div>
      )}

      <RecentlyViewed briefLocation={briefLocation} />

      {viewData.alsoBookedCrossCategory && viewData.alsoBookedCrossCategory.length > 0 && (
        <Shelf className={styles.wideNegativeMarginShelf}>
          <div className={styles.shelfHed}>
            <H.Title2 presentation="h3">
              <span className={styles.serif}>Also booked</span> by couples{' '}
              {viewData.mostRecentBooked ? `who chose ${viewData.mostRecentBooked.vendorName}` : ''}
            </H.Title2>
          </div>
          <div className={styles.bookedCardList}>
            {viewData.alsoBookedCrossCategory.slice(0, 4).map((vendor, index) => {
              return (
                <div className={styles.cardWrapper} key={vendor.storefrontUuid}>
                  <VendorCard
                    vendor={storefrontCardViewToVendorCardVendor(vendor)}
                    position={index + 1}
                    showDetails={false}
                    showOverlayBadge={false}
                    onClick={() => {
                      setLocalStorage(
                        `inquiry_entrypoint_${vendor.storefrontUuid}`,
                        TrackingProductLocation.SUGGESTED_CROSS_CATEGORY_VENDORS
                      );
                    }}
                    showCarousel={isDesktop()}
                    trackClick={false}
                  />
                  <LinkV2
                    noTextTransform
                    arrow
                    arrowPosition="postfix"
                    target="_blank"
                    href={
                      vendorSearchUrl(
                        vendor.taxonomyNodeKey as SearchableVendorTaxonomyKey,
                        briefLocation.slug
                      ) || '/wedding-vendors'
                    }
                  >
                    More {getVendorWordPlural(vendor.taxonomyNodeKey)}
                  </LinkV2>
                </div>
              );
            })}
          </div>
        </Shelf>
      )}

      {viewData.alsoInquiredSameCategory &&
        viewData.alsoInquiredSameCategory.length > 0 &&
        viewData.mostRecentInquiry && (
          <Shelf>
            <div className={styles.shelfHed}>
              <div>
                <H.Title2 presentation="h3">
                  <span className={styles.serif}>Similar to</span>{' '}
                  {viewData.mostRecentInquiry.storefront.vendorName}
                </H.Title2>
                <P.BodyBase className={styles.body}>
                  Inquire with just one click! We&apos;ve saved all your details.{' '}
                  <LinkV2
                    className={styles.modalTrigger}
                    type="button"
                    role="button"
                    noTextTransform
                    onClick={() =>
                      onBookedVendorsClick(
                        viewData.mostRecentInquiry?.storefront.taxonomyNode.key as VendorTaxonomyKey
                      )
                    }
                  >
                    Already booked your{' '}
                    {getVendorWordSingular(viewData.mostRecentInquiry.storefront.taxonomyNode.key)}?
                    Add it here
                  </LinkV2>
                </P.BodyBase>
              </div>
            </div>

            <SideScrollingCardList
              cardList={viewData.alsoInquiredSameCategory
                .slice(0, 8)
                .map(storefrontCardViewToVendorCardVendor)}
              trackingProductLocation={TrackingProductLocation.SUGGESTED_SAME_CATEGORY_VENDORS}
              showVendorType={
                viewData.alsoInquiredSameCategory[0].taxonomyNodeKey === BANDS_DJS_TAXONOMY_KEY
              }
            />
          </Shelf>
        )}

      {(inquiryDraftStorefrontList || favoriteStorefrontList) && (
        <Shelf>
          <div className={styles.smallVendorCardSections}>
            {inquiryDraftStorefrontList && (
              <div className={styles.smallVendorCardSection}>
                <H.Title2 presentation="h3">
                  Finish up <span className={styles.serif}>these inquiries</span>
                </H.Title2>
                <SmallVendorCardList
                  cardList={inquiryDraftStorefrontList}
                  trackingProductLocation={TrackingProductLocation.INQUIRY_DRAFTS}
                  onSuccess={onInquiryDraftCompletion}
                />
              </div>
            )}

            {favoriteStorefrontList && (
              <div className={styles.smallVendorCardSection}>
                <div className={styles.shelfHed}>
                  <H.Title2 presentation="h3">
                    Reach out to <span className={styles.serif}>your favorites</span>
                  </H.Title2>
                  {(viewData?.nonInquiredFavorites?.length || 0) >
                    favoriteStorefrontList.length && (
                    <LinkV2
                      noTextTransform
                      arrow
                      arrowPosition="postfix"
                      href="/favorites/vendors"
                      className={styles.link}
                    >
                      View all
                    </LinkV2>
                  )}
                </div>
                <SmallVendorCardList
                  cardList={favoriteStorefrontList}
                  trackingProductLocation={TrackingProductLocation.VENDOR_FAVORITES}
                  onSuccess={onInquiryDraftCompletion}
                />
              </div>
            )}
          </div>
        </Shelf>
      )}

      {viewData.showTopPicks && Array.isArray(viewData.topPicks) && viewData.topPicks?.length > 0 && (
        <Shelf className={styles.negativeMarginShelf}>
          <div className={styles.shelfHed}>
            <H.Title2 presentation="h3">
              Our{' '}
              <span className={styles.serif}>
                top {getVendorWordSingular(viewData.topPicks[0].taxonomyNodeKey)} picks
              </span>{' '}
              based on your preferences
            </H.Title2>

            <LinkV2
              noTextTransform
              arrow
              arrowPosition="postfix"
              target="_blank"
              href={
                vendorSearchUrl(
                  viewData.topPicks[0].taxonomyNodeKey as SearchableVendorTaxonomyKey,
                  briefLocation.slug
                ) || '/wedding-vendors'
              }
            >
              View all {getVendorWordPlural(viewData.topPicks[0].taxonomyNodeKey)} in{' '}
              {briefLocation.name}, {briefLocation.stateCode}
            </LinkV2>
          </div>
          <div className={styles.topPicksCardList}>
            {viewData.topPicks.slice(0, topPicksDisplayCount).map((vendor, index) => {
              return (
                <div className={cx(styles.cardWrapper)} key={`top-picks-${vendor.storefrontUuid}`}>
                  <VendorCard
                    vendor={storefrontCardViewToVendorCardVendor(vendor)}
                    position={index + 1}
                    showBadgeList
                    showDetails={false}
                    onClick={() => {
                      setLocalStorage(
                        `inquiry_entrypoint_${vendor.storefrontUuid}`,
                        TrackingProductLocation.TOP_PICKS
                      );
                    }}
                    showCarousel={isDesktop()}
                    trackClick={false}
                    showVendorType={vendor.taxonomyNodeKey === BANDS_DJS_TAXONOMY_KEY}
                  />
                </div>
              );
            })}
            {showMTFMmodule && (
              <div className={cx(styles.cardWrapper)}>
                <MiniQuizCard
                  className={styles.miniQuiz}
                  taxonomyKey={viewData.topPicks[0].taxonomyNodeKey as SearchableVendorTaxonomyKey}
                  onQuizSubmitted={onMiniQuizSubmited}
                />
              </div>
            )}
          </div>
        </Shelf>
      )}
      {viewData.showTopPicks &&
        Array.isArray(viewData.topPicks) &&
        viewData.topPicks?.length === 0 &&
        topInquiredList !== null &&
        topInquiredList.length > 0 && (
          <Shelf>
            <div className={styles.shelfHed}>
              <div>
                <H.Title2 presentation="h3">
                  Most <span className={styles.serif}>popular</span> vendors in {briefLocation.name}
                  , {briefLocation.stateCode}
                </H.Title2>
              </div>

              {topInquiredList.length > 5 && (
                <LinkV2
                  noTextTransform
                  arrow
                  arrowPosition="postfix"
                  target="_blank"
                  href={
                    vendorSearchUrl(VENUES_TAXONOMY_KEY, briefLocation.slug) || '/wedding-vendors'
                  }
                >
                  View all
                </LinkV2>
              )}
            </div>

            <SideScrollingCardList
              cardList={topInquiredList.slice(0, 8).map(storefrontCardViewToVendorCardVendor)}
              trackingProductLocation={TrackingProductLocation.MOST_INQUIRED}
            />
          </Shelf>
        )}
    </div>
  );
};

export const PersonalizedContent = ({
  briefLocation,
  setHasPersonalizedContentLoaded,
  isClpTest,
}: {
  briefLocation: NotNullMembersBriefLocation;
  setHasPersonalizedContentLoaded: Dispatch<SetStateAction<boolean>>;
  isClpTest: boolean;
}) => {
  return (
    <ErrorBoundary>
      <RecentlyViewedStorefrontsProvider
        includeGallery={isDesktop()}
        location={{ vendorMarketId: briefLocation.vendorMarket.id }}
      >
        <Content
          briefLocation={briefLocation}
          setHasPersonalizedContentLoaded={setHasPersonalizedContentLoaded}
          isClpTest={isClpTest}
        />
      </RecentlyViewedStorefrontsProvider>
    </ErrorBoundary>
  );
};
